import type { ECUCataSelectProgramItemInterface, ECUCataSelectProgramOptionInterface } from "../models/ECUCInterfaces";
import { FNV3X_ARCHITECTURES } from "./constants";

export function hasNonEmptyUnfilteredProgramOptions(programs: Partial<ECUCataSelectProgramItemInterface>): boolean {
    return Array.isArray(programs.unfilteredOptions) && programs.unfilteredOptions.length > 0;
}

export function resolveProgramOptionsWithFallback(
    programs: Partial<ECUCataSelectProgramItemInterface>
): ECUCataSelectProgramOptionInterface[] {
    if (hasNonEmptyUnfilteredProgramOptions(programs)) {
        return programs.unfilteredOptions as ECUCataSelectProgramOptionInterface[];
    }

    if (Array.isArray(programs.options)) {
        return programs.options as ECUCataSelectProgramOptionInterface[];
    }

    return [];
}

// Treats FNV3.2 and FNV3.4 as the same architecture group, so programs from either remain
// selectable together. Falls back to returning all unfiltered options when architecture
// metadata is incomplete/mismatched, or when selected programs span multiple non-grouped architectures.
export function filterProgramOptionsByArchitectureGroup(
    unfilteredProgramOptions: ECUCataSelectProgramOptionInterface[],
    selectedProgramOptions: ECUCataSelectProgramOptionInterface[]
): ECUCataSelectProgramOptionInterface[] {
    if (selectedProgramOptions.length === 0) {
        return unfilteredProgramOptions;
    }

    const selectedElectricalArchitectures = Array.from(
        new Set(
            selectedProgramOptions
                .map((option) => option?.electricalArchitecture)
                .filter((value): value is string => typeof value === "string" && value !== "")
        )
    );

    const isWithinFNV3XGroup = selectedElectricalArchitectures.length > 0 &&
        selectedElectricalArchitectures.every((architecture) => FNV3X_ARCHITECTURES.includes(architecture));

    let architecturesToMatch: string[] | null = null;
    if (selectedElectricalArchitectures.length === 1) {
        const selectedElectricalArchitecture = selectedElectricalArchitectures[0];
        architecturesToMatch = FNV3X_ARCHITECTURES.includes(selectedElectricalArchitecture)
            ? FNV3X_ARCHITECTURES
            : [selectedElectricalArchitecture];
    } else if (isWithinFNV3XGroup) {
        architecturesToMatch = FNV3X_ARCHITECTURES;
    }

    if (!architecturesToMatch) {
        return unfilteredProgramOptions;
    }

    const architectureMatchedOptions = unfilteredProgramOptions.filter(
        (option) => architecturesToMatch.includes(option.electricalArchitecture)
    );

    // Keep options available if architecture metadata is incomplete or mismatched.
    return architectureMatchedOptions.length > 0 ? architectureMatchedOptions : unfilteredProgramOptions;
}